import {
  IconCopy, IconDownload, IconEdit, IconLink, IconMove, IconShare, IconTrash,
} from '../../lib/icons.tsx';
import { previewKindOf } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import type { MenuAction } from './ContextMenu.tsx';
import type { ApiNode } from '../../api/types.ts';
import type { useFolderActions } from '../../hooks/useFolderActions.ts';

interface Input {
  node: ApiNode;
  selectedPaths: string[];
  canEdit: boolean;
  currentPath: string;
  actions: ReturnType<typeof useFolderActions>;
  onRename: () => void;
  onDetails: () => void;
  onPreview: () => void;
  onShareFolder: () => void;
  canShareFolder: boolean;
  /** 공유 기능이 꺼져 있으면 관련 항목을 아예 만들지 않는다 */
  sharingEnabled: boolean;
}

/** 컨텍스트 메뉴 항목 구성. 다중 선택이면 항목별 동작 대신 일괄 동작을 노출한다. */
export function buildActions(input: Input): Array<MenuAction | 'separator'> {
  const { node, selectedPaths, canEdit, currentPath, actions } = input;
  const multiple = selectedPaths.length > 1;
  const canPreview = node.type === 'file' && previewKindOf(node.name, node.mime) !== 'none';

  return [
    ...(multiple ? [] : [{
      label: node.type === 'folder' ? '열기' : canPreview ? '미리보기' : '다운로드',
      run: () => { if (canPreview) input.onPreview(); else void actions.download([node.path], node.name); },
    } satisfies MenuAction]),
    {
      label: multiple ? `${selectedPaths.length}개 다운로드 (ZIP)` : '다운로드',
      icon: <IconDownload size={16} />,
      run: () => {
        if (multiple || node.type === 'folder') void actions.downloadZip(selectedPaths, node.name);
        else void actions.download([node.path], node.name);
      },
    },
    'separator',
    {
      label: '이름 변경',
      icon: <IconEdit size={16} />,
      shortcut: 'F2',
      disabled: !canEdit || multiple,
      run: input.onRename,
    },
    {
      label: '사본 만들기',
      icon: <IconCopy size={16} />,
      disabled: !canEdit,
      run: () => { actions.copy.mutate({ paths: selectedPaths, targetFolder: currentPath }); },
    },
    {
      label: '상위 폴더로 이동',
      icon: <IconMove size={16} />,
      disabled: !canEdit || currentPath === '',
      run: () => {
        const parent = currentPath.slice(0, currentPath.lastIndexOf('/'));
        actions.move.mutate({ paths: selectedPaths, targetFolder: parent });
      },
    },
    'separator',
    ...(input.sharingEnabled ? [
      {
        label: '공유 링크',
        icon: <IconLink size={16} />,
        disabled: !canEdit || multiple,
        run: () => { input.onDetails(); toast.info('오른쪽 패널의 공유 탭에서 링크를 만들 수 있습니다'); },
      } satisfies MenuAction,
      {
        label: '폴더 공유하기',
        icon: <IconShare size={16} />,
        disabled: !input.canShareFolder || multiple || node.type !== 'folder',
        run: input.onShareFolder,
      } satisfies MenuAction,
    ] : []),
    {
      label: '정보 보기',
      disabled: multiple,
      run: input.onDetails,
    },
    'separator',
    {
      label: multiple ? `${selectedPaths.length}개 삭제` : '삭제',
      icon: <IconTrash size={16} />,
      shortcut: 'Del',
      danger: true,
      disabled: !canEdit,
      run: () => { actions.remove.mutate(selectedPaths); },
    },
  ];
}
